import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Home as IconHome } from 'react-feather'
import { Link, useHistory, useParams } from 'react-router-dom'
import Select from 'react-select'
import _ from 'lodash'
import * as yup from 'yup'
import styled from 'styled-components'
import { shade } from 'polished'
import useAxios from 'axios-hooks'
import { toast } from 'react-toastify'
import { Formik, Form, useFormikContext, Field as FormikField } from 'formik'
import { Input, Row, Button, CustomInput, Card } from 'reactstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { Col, Show } from '~/components/commons'
import Breadcrumb from '~/components/Breadcrumb'
import { TableContainer, validations } from './commons'
import { Field, OptionalField } from '../Posts/commons'
import MultiSelect from '~/components/MultiSelect'
import UploadPhoto from '~/components/ecto/UploadPhoto'

import {
  defaultUserPermissions,
  defaultUserPermissionIcons,
} from '~/constants/users'
import { Alert } from '~/components/Alert'
import { CLIENT_ADMIN, SUPER_ADMIN, ROLES, ADMIN } from '~/constants/roles'

const initialValues = {
  user: {
    name: '',
    email: '',
    password: '',
    userPermission: {},
    permissions: {},
    userNotification: {},
    ip: '',
    relationType: 'PROJECT',
    role: '',
  },
  ipChecked: false,
  accountId: '',
  passwordConfirmation: '',
  avatarImage: '',
  projectsIds: [],
  locationIds: [],
}

const validationSchema = isUpdate =>
  yup.lazy(({ user }) =>
    yup.object().shape({
      user:
        isUpdate && !user.password
          ? validations.userWithoutPass
          : validations.user,
      passwordConfirmation: user.password
        ? validations.passwordConfirmation
        : null,
    })
  )

const FormContext = React.createContext()

function useOptions(endpoint, getData, buildOption, config) {
  const [requestState, refetch] = useAxios(endpoint, config)

  const options = useMemo(() => {
    const { data, loading, error } = requestState
    if (!data || loading || error) {
      return []
    }

    return getData(data)?.map(item => buildOption(item))
  }, [requestState, getData, buildOption])
  return [options, refetch, requestState]
}

function PermissionsTable({ isDisabled }) {
  const { setFieldValue, values } = useFormikContext()
  const { userPermission, userIsAppAdmin, userIsAdmin, isSelf } = useContext(
    FormContext
  )
  const formPermissions = values.user.userPermission
  const permissions = [...defaultUserPermissions].filter(permissionKey => {
    return userIsAppAdmin || userIsAdmin || userPermission[permissionKey]
  })

  return (
    <div style={{ flexGrow: '1' }}>
      <StyledHeader>
        <FormattedMessage id="Permissions" />
      </StyledHeader>
      <TableContainer style={{ marginTop: '10px' }}>
        <div className="table-responsive">
          <table className="table table-borderless table-striped mb-0">
            <tbody>
              {permissions?.map((permissionKey, index) => {
                const Icon = defaultUserPermissionIcons[permissionKey]
                const name =
                  permissionKey === 'edits'
                    ? 'Pending Edits'
                    : _.upperFirst(permissionKey)
                return (
                  <tr key={index}>
                    <td
                      className="pl-2"
                      style={{
                        textTransform: 'capitalize',
                        fontSize: '16px',
                        fontWeight: 500,
                        fontFamily: 'Montserrat',
                      }}
                    >
                      <div
                        className="d-flex w-100 h-100 align-items-center"
                        style={{
                          gap: '1em',
                          marginLeft:
                            permissionKey === 'keywords' ? '2em' : '0',
                        }}
                      >
                        <Icon size="20" />
                        <FormattedMessage id={name} />
                      </div>
                    </td>

                    {!isSelf && (
                      <td key={index}>
                        <div className="custom-checkbox custom-control d-flex justify-content-end">
                          <CustomInput
                            type="switch"
                            id={`user-userPermission-${permissionKey}`}
                            name={`user.userPermission.${permissionKey}`}
                            checked={formPermissions[permissionKey]}
                            disabled={isDisabled}
                            onChange={() => {
                              if (
                                permissionKey === 'insights' &&
                                formPermissions[permissionKey]
                              ) {
                                setFieldValue(
                                  `user.userPermission.keywords`,
                                  false
                                )
                              }
                              setFieldValue(
                                `user.userPermission.${permissionKey}`,
                                !formPermissions[permissionKey]
                              )
                            }}
                            inline
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </TableContainer>
    </div>
  )
}

function CreateNewUserForm() {
  const intl = useIntl()
  const { setFieldValue, initialValues, values } = useFormikContext()
  const [selects, setSelects] = useState({})
  const {
    userProfile,
    projects,
    accounts,
    locations,
    setSelectedAccount,
    isUpdate,
    isSelf,
    userActive,
    userIsAppAdmin,
    userIsClientAdmin,
    setUserActive,
    projectLoad,
    accountLoad,
    locationLoad,
  } = useContext(FormContext)

  const isLesserRole = userProfile?.role < values.user?.role?.value

  const isMobile = window.matchMedia('(max-width: 767px)').matches

  const statusOptions = useMemo(() => {
    const userStatus = [
      { label: intl.formatMessage({ id: 'Activated' }), value: true },
      { label: intl.formatMessage({ id: 'Deactivated' }), value: false },
    ]

    return userStatus
  }, [intl])

  const roleOptions = useMemo(() => {
    const userRoles = Object.entries(ROLES)
      .map(([key, value]) => {
        return {
          label: value,
          value: key,
        }
      })
      .filter(role => userProfile?.role >= role.value)

    return userRoles
  }, [userProfile])

  useEffect(() => {
    const fields = [
      'accountId',
      'projectsIds',
      'locationIds',
      'user.relationType',
    ]
    fields.forEach(async field => {
      setFieldValue(field, _.get(initialValues, field))
      setSelects(old => ({
        ...old,
        [field]: _.get(initialValues, field),
      }))
    })
  }, [initialValues, setFieldValue])

  const handleFilterValueChange = useCallback(
    (filter, options) => {
      if (filter === 'accountId') {
        setSelectedAccount(options?.value)
        setSelects(old => ({ ...old, projectsIds: [], locationIds: [] }))
      }

      if (filter === 'userStatus') {
        setUserActive(options.value)
        setFieldValue('activateUser', options.value)
        setFieldValue('desactivateUser', !options.value)
      }

      setFieldValue(filter, options)
      setSelects(old => ({ ...old, [filter]: options }))
    },
    [setFieldValue, setSelects, setSelectedAccount, setUserActive]
  )

  return (
    <>
      <Row className="mb-1">
        <Col
          md="6"
          className="px-1"
          style={{
            paddingTop:
              isUpdate &&
              !isMobile &&
              (userIsAppAdmin || userIsClientAdmin) &&
              '30px',
          }}
        >
          <Field
            disabled={!userActive}
            className="mb-1"
            name="user.name"
            label="Name"
            render={Input}
            required
          />
          <Field
            disabled={!userActive}
            className="mb-1"
            name="user.email"
            label="Email"
            render={Input}
            type="email"
            required
          />
        </Col>
        <Col md="6" className="px-1">
          <UploadPhoto
            disabled={!userActive}
            name="avatarImage"
            label="User photo"
            onChange={v => setFieldValue('avatarImage', v)}
            showHelperText
            helperText={`Allowed JPG or PNG. Max size of 800kB.`}
            helperText2={`This photo visible only in Locasty's dashboard.`}
            required
            maxSize={'110px'}
            style={{
              marginTop:
                isUpdate &&
                !isMobile &&
                (userIsAppAdmin || userIsClientAdmin) &&
                '-40px',
            }}
            initialImage={initialValues.user.avatar}
          />
        </Col>
      </Row>
      <Row>
        <Col md="6" className="px-1 mb-1">
          <Field
            disabled={!userActive}
            className="mb-1"
            name="user.password"
            label={isUpdate ? 'New Password' : 'Password'}
            render={Input}
            type="password"
            required
            uuncontrolled="true"
          />
          <Field
            disabled={!userActive}
            className="mb-1"
            name="passwordConfirmation"
            label={isUpdate ? 'Confirm New Password' : 'Confirm Password'}
            render={Input}
            type="password"
            required
            uncontrolled="true"
          />
        </Col>
        <Col
          md="6"
          className="px-1"
          style={{
            marginTop:
              isUpdate &&
              !isMobile &&
              (userIsAppAdmin || userIsClientAdmin) &&
              '-75px',
          }}
        >
          <Show condition={isUpdate && (userIsAppAdmin || userIsClientAdmin)}>
            <label>
              <FormattedMessage id="Status" />
            </label>
            <Select
              isDisabled={isLesserRole || isSelf}
              className="basic-single mb-1"
              name="user.status"
              classNamePrefix="select"
              options={statusOptions}
              value={statusOptions[userActive ? 0 : 1]}
              onChange={value => handleFilterValueChange('userStatus', value)}
            />
          </Show>
          <label>
            <FormattedMessage id="Role" />
          </label>
          <Select
            isDisabled={
              (!userIsAppAdmin && !userIsClientAdmin) || isSelf || !userActive
            }
            name="user.role"
            className="basic-single mb-1"
            classNamePrefix="select"
            value={values.user.role}
            placeholder={<FormattedMessage id="Select the role" />}
            onChange={value => setFieldValue('user.role', value)}
            options={roleOptions}
            menuPortalTarget={document.querySelector('body')}
          />
          <Show condition={userIsAppAdmin || userIsClientAdmin}>
            {/* Do not show to user */}
            <OptionalField
              value={values.user.ip}
              disabled={values.ipChecked || !userActive}
              setDisabled={value => setFieldValue('ipChecked', value)}
              name="user.ip"
              label="IP Restriction"
              placeholder="Ex: 192.168.0.0;192.168..."
            />
          </Show>
        </Col>
      </Row>
      <StyledHeader>
        <FormattedMessage id="Access" />
      </StyledHeader>
      <Row>
        <Col md="6" className="px-1 mb-1">
          <label>
            <FormattedMessage id="Vinculate Account" />
          </label>
          <Select
            isDisabled={!userActive}
            name="accountId"
            className="basic-single"
            classNamePrefix="select"
            isLoading={accountLoad}
            value={selects.accountId}
            placeholder={<FormattedMessage id="Select the account" />}
            onChange={value => handleFilterValueChange('accountId', value)}
            options={accounts}
            menuPortalTarget={document.querySelector('body')}
          />
        </Col>
        <Col md="6" className="px-1">
          <RadioSelect>
            <div>
              <input
                type="radio"
                id="project"
                name="type"
                value="PROJECT"
                onChange={e =>
                  handleFilterValueChange('user.relationType', e.target.value)
                }
                checked={values?.user?.relationType === 'PROJECT'}
              />
              <label htmlFor="project">
                <FormattedMessage id="Project(s)" />
              </label>
            </div>
            <div>
              <input
                type="radio"
                id="location"
                name="type"
                value="LOCATION"
                onChange={e =>
                  handleFilterValueChange('user.relationType', e.target.value)
                }
                checked={values?.user?.relationType === 'LOCATION'}
              />
              <label htmlFor="location">
                <FormattedMessage id="Location(s)" />
              </label>
            </div>
          </RadioSelect>
          {values?.user?.relationType === 'PROJECT' ? (
            <MultiSelect
              isDisabled={!userActive}
              uniqueKey="projectsIds"
              placeholder={<FormattedMessage id="All projects" />}
              options={projects}
              loading={projectLoad}
              value={selects?.projectsIds}
              onChange={options =>
                handleFilterValueChange('projectsIds', options)
              }
            />
          ) : (
            <MultiSelect
              isDisabled={!userActive}
              uniqueKey="locationIds"
              placeholder={<FormattedMessage id="All locations" />}
              options={locations}
              loading={locationLoad}
              value={selects?.locationIds}
              filterOption={(candidate, input) => {
                const normalizedCandidate = candidate.label.name
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
                  .toLowerCase()
                const normalizedInput = input
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
                  .toLowerCase()
                const normalizedAddress = candidate.label.data.storefrontAddress?.addressLines?.[0]
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
                  .toLowerCase()

                return (
                  normalizedCandidate.includes(normalizedInput) ||
                  normalizedAddress.includes(normalizedInput)
                )
              }}
              onChange={options =>
                handleFilterValueChange('locationIds', options)
              }
              renderOption={({ label: location }) => (
                <div className="col">
                  <span>{location?.name ?? ''}</span>
                  <small style={{ display: 'block', color: '#8D8D8D' }}>
                    {location?.data?.storefrontAddress?.addressLines?.join(' ')}
                    , {location?.data?.storefrontAddress?.locality} -{' '}
                    {location?.data?.storefrontAddress?.administrativeArea}
                  </small>
                </div>
              )}
            />
          )}
        </Col>
      </Row>
      <Row className="px-1 mb-2 mt-2">
        <TwoColumnResponsive>
          <Col className="mb-1" style={{ minWidth: '50%' }}>
            <StyledHeader>
              <FormattedMessage id="Email Notifications" />
            </StyledHeader>
            <div className="custom-checkbox custom-control d-flex">
              <FormikField
                disabled={!userActive}
                id="user.userNotification.monthlyReport"
                className="custom-control-input"
                type="checkbox"
                name="user.userNotification.monthlyReport"
              />
              <CheckBoxlabel
                className="custom-control-label"
                htmlFor="user.userNotification.monthlyReport"
              >
                <div style={{ fontSize: '1.2em', fontWeight: '500' }}>
                  <FormattedMessage id="notification.monthlyReport.title" />
                </div>
                <div style={{ fontSize: '1.2em', color: '#718096' }}>
                  <FormattedMessage id="notification.monthlyReport.description" />
                </div>
              </CheckBoxlabel>
            </div>
          </Col>
          <PermissionsTable isDisabled={!userActive} />
        </TwoColumnResponsive>
      </Row>
      <CreateButton type="submit">
        <FormattedMessage id={isUpdate ? 'Update User' : 'Create New User'} />
      </CreateButton>
    </>
  )
}

function UsersCreate() {
  const [alertVisible, setAlertVisible] = useState(false)
  const [modalMessage, setModalMessage] = useState('')
  const history = useHistory()
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [formData, setFormData] = useState(null)
  const [isUpdate, setIsUpdate] = useState(false)
  const [isSelf, setIsSelf] = useState(false)
  const [userActive, setUserActive] = useState(true)
  const { userId } = useParams()

  const userProfile = useSelector(state => state?.user?.profile)
  const userPermission = useSelector(
    state => state?.user?.profile?.userPermission
  )
  const userIsAppAdmin = userProfile?.role === SUPER_ADMIN
  const userIsClientAdmin = userProfile?.role === CLIENT_ADMIN
  const userIsAdmin = userProfile?.role === ADMIN

  function openModal() {
    setModalMessage(`User successfully ${isUpdate ? 'updated' : 'created'}!`)
    setAlertVisible(true)
  }

  const profileAccount = useSelector(state => state?.user?.profile?.account?.id)

  const [projects, , { loading: projectLoad }] = useOptions(
    userIsAppAdmin
      ? `/accounts/${selectedAccount}/projects/options`
      : `/accounts/${profileAccount}/projects/options`,
    data => data.projects,
    project => ({
      label: project.name.replace(/^\.INTERNAL - /, ''),
      value: [project.id],
    }),
    {
      useCache: false,
      manual: userIsAppAdmin ? !selectedAccount : false,
    }
  )

  const [locations, , { loading: locationLoad }] = useOptions(
    userIsAppAdmin
      ? `/accounts/${selectedAccount}/locations/options`
      : `/accounts/${profileAccount}/locations/options`,
    data => data.locations,
    location => ({
      label: location,
      value: [location.id],
    }),
    {
      useCache: false,
      manual: userIsAppAdmin ? !selectedAccount : false,
    }
  )

  const [accounts, , { loading: accountLoad }] = useOptions(
    userIsAppAdmin ? '/admin/accounts' : `/accounts/${profileAccount}`,
    data => (userIsAppAdmin ? data : [data]),
    account => ({
      label: account.name,
      value: [account.id],
    }),
    { useCache: false }
  )

  const [, createUser] = useAxios(
    {
      url: '/users',
      method: 'POST',
    },
    { manual: true }
  )

  const [, updateUser] = useAxios(
    {
      url: `/users/${userId}`,
      method: 'PATCH',
    },
    { manual: true }
  )

  const [, uploadFile] = useAxios(
    {
      url: '/upload',
      method: 'POST',
    },
    { manual: true }
  )

  const [, fetchUserData] = useAxios(`/users/${userId}`, { manual: true })

  const fillUserData = useCallback(async () => {
    const { data } = await fetchUserData()

    if (userProfile?.role < data.role) history.push('/')
    if (userProfile?.id === data.id) setIsSelf(true)

    setSelectedAccount([data.account.id])

    const user = {
      name: data.name,
      email: data.email,
      ip: data.ip,
      avatar: data.avatar,
      userPermission: data?.userPermission,
      permissions: data?.permissions,
      userNotification: data.userNotification,
      relationType: data?.relationType,
      role: { label: ROLES[data.role], value: data.role },
      password: '',
    }

    setFormData({
      user,
      passwordConfirmation: '',
      accountId: { label: data.account.name, value: data.account.id },
      projectsIds:
        data?.relationType === 'PROJECT'
          ? data.projects.map(project => ({
              label: project.name,
              value: [project.id],
            }))
          : [],
      locationIds:
        data?.relationType === 'LOCATION'
          ? data.locations.map(location => ({
              label: location.name,
              value: [location.id],
            }))
          : [],
      ipChecked: !data.ip,
    })

    setUserActive(!data.deletedAt)

    setIsUpdate(true)
  }, [fetchUserData, userProfile, history])

  useEffect(() => {
    if (userId) {
      fillUserData()
    }
  }, [userId, fillUserData])

  async function handleSubmit({
    user,
    avatarImage,
    projectsIds,
    locationIds,
    accountId,
    desactivateUser,
    activateUser,
  }) {
    let uploadedImageLink

    if (avatarImage?.type) {
      const fd = new FormData()
      fd.append('file', avatarImage)
      await uploadFile({ data: fd })
        .then(({ data }) => {
          uploadedImageLink = data.uri
        })
        .catch(e => {
          toast.error('An error occurred during upload image! Try again later.')
          console.error(e)
          throw new Error()
        })
    }

    if (avatarImage === null) {
      uploadedImageLink = ''
    }

    if (uploadedImageLink === undefined) {
      uploadedImageLink = user.avatar
    }

    user.role = user.role.value

    try {
      var formData = {
        ...user,
        avatar: uploadedImageLink,
        projectsIds: projectsIds?.map(project => project?.value[0]),
        locationIds: locationIds?.map(location => location?.value[0]),
        accountId: _.isArray(accountId?.value)
          ? accountId?.value?.[0]
          : accountId?.value,
        desactivateUser,
        activateUser,
      }

      const submitAction = isUpdate ? updateUser : createUser

      await submitAction({ data: formData })

      openModal()
    } catch (error) {
      console.error(
        `Error on ${isUpdate ? 'update' : 'creation'} of user`,
        error.response
      )
      const { data } = error.response
      toast.error(data.error ?? data.message)
    }
  }

  return (
    <Col>
      <Breadcrumb
        className="mb-2"
        paths={[
          {
            render: () => (
              <Link className="d-flex align-items-center" to="/">
                <IconHome size={20} />
              </Link>
            ),
          },
          {
            render: () => (
              <Link className="d-flex align-items-center" to="/users">
                <FormattedMessage id="Users" />
              </Link>
            ),
          },
          { text: isUpdate ? 'Update User' : 'Create New User' },
        ]}
      />

      <Card style={{ padding: '30px 40px' }}>
        <StyledHeader>
          <FormattedMessage id={isUpdate ? 'Update User' : 'Create New User'} />
        </StyledHeader>
        <FormContext.Provider
          value={{
            userProfile,
            projects,
            accounts,
            locations,
            setSelectedAccount,
            isUpdate,
            isSelf,
            userActive,
            setUserActive,
            userPermission,
            userIsAppAdmin,
            userIsAdmin,
            userIsClientAdmin,
            projectLoad,
            accountLoad,
            locationLoad,
          }}
        >
          <Formik
            initialValues={formData ?? initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema(isUpdate)}
            validateOnBlur={false}
            validateOnChange={false}
            validateOnMount={false}
            enableReinitialize
          >
            {() => (
              <Form>
                <CreateNewUserForm />
              </Form>
            )}
          </Formik>
        </FormContext.Provider>
      </Card>
      <Alert
        type="success"
        visible={alertVisible}
        onClose={() => history.push('/users')}
        onCancel={() => setAlertVisible(false)}
        hasCancelBtn={true}
        cancelBtnText="Close"
        confirmBtnText="Go to users"
        body={modalMessage}
        title="Success"
      />
    </Col>
  )
}

const RadioSelect = styled.div`
  display: flex;
  height: 20px;
  gap: 1em;
  accent-color: #7367f0;

  div {
    display: flex;
  }
`

const TwoColumnResponsive = styled.div`
  width: 100%;
  display: flex;
  gap: 1em;
  flex-direction: column;
  justify-content: space-between;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`

const CreateButton = styled(Button)`
  font-size: 18px;
  font-weight: 500;
  color: white;
  background-color: ${props => props.theme.color.primary} !important;
  margin-top: 30px;
  text-transform: uppercase;

  &:hover {
    background-color: ${props => shade(0.2, props.theme.color.primary)};
    color: white;
  }

  svg {
    margin-right: 12px;
  }
`

const CheckBoxlabel = styled.label`
  &:before {
    width: 1.3rem;
    height: 1.3rem;
  }

  &:after {
    width: 1.3rem;
    height: 1.3rem;
  }
`

const StyledHeader = styled.h3`
  font-size: 20px;
  font-weight: 400;
  font-family: Montserrat;
`

export default UsersCreate
